import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import Banner from '@zenbusiness/zui/Banner';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import {
  RELEASE_THREAD_BANK_COMPLIANCE,
  HIGH_RISK_UNIT_THREAD_BANK_CUSTOMERS
} from '#client/constants/experimentsFlags';
import { MODAL_NAMES } from '#client/constants/modals';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useModalContext from '#client/hooks/useModalContext';
import {
  defaultPayloadBannerDisplayed,
  defaultPayloadButtonPressed
} from '#client/hooks/useTrackEvent';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { PATCH_APPLICATION_INIT_DATA_MUTATION } from '#client/mutations';
import { BannerWrapperStyled } from '#client/pages/SharedStyles';
import { GET_APPLICATION_ADDITIONAL_INFO_QUERY } from '#client/queries';

const REQUIRED_FIELDS = [
  'businessIndustry',
  'accountPurpose',
  'sourceOfFunds',
  'transactionVolume'
];

/**
 * Checks whether all required CDD/EDD fields have been filled in.
 * @param {object} application - The application data from the GraphQL query
 * @returns {boolean} true if all required fields are filled
 */
function isAdditionalInfoComplete(application) {
  if (!application) {
    return false;
  }

  const basicFieldsFilled = REQUIRED_FIELDS.every(
    (field) =>
      application[field] !== null && application[field] !== undefined && application[field] !== ''
  );

  const regulatedFieldFilled =
    application.isRegulated !== null && application.isRegulated !== undefined;
  const usNexusFilled =
    application.usNexus !== null && application.usNexus !== undefined && application.usNexus !== '';

  return basicFieldsFilled && regulatedFieldFilled && usNexusFilled;
}

/**
 * Banner component that prompts high-risk customers to provide additional banking information
 *
 * This banner appears on the banking dashboard when:
 * - User is marked as high-risk by Unit
 * - Additional CDD/EDD information is required
 * - Feature flag 'release_thread_bank_compliance' is enabled
 * - The required additional info fields have not been filled yet
 *
 * @returns {any} Banner component or null if conditions not met
 */
export default function AdditionalInfoBanner() {
  const { openModal } = useModalContext();
  const { partnerCustomerId } = useUnitAccountId();
  const { businessEntityUuid } = useBusinessEntity();

  const [isFeatureEnabled] = useFeatureFlag(RELEASE_THREAD_BANK_COMPLIANCE);
  const highRiskCustomers = useExperimentFlag(HIGH_RISK_UNIT_THREAD_BANK_CUSTOMERS, []);

  const isInHighRiskList = highRiskCustomers?.includes(partnerCustomerId);

  const { data, loading } = useQuery(GET_APPLICATION_ADDITIONAL_INFO_QUERY, {
    noThrow: true,
    variables: {
      input: {
        businessUuid: businessEntityUuid
      }
    },
    skip: !businessEntityUuid || !isFeatureEnabled || !isInHighRiskList,
    refetchAfterMutations: [PATCH_APPLICATION_INIT_DATA_MUTATION]
  });

  // Don't show banner if feature is disabled or customer is not in high-risk list
  if (!isFeatureEnabled || !isInHighRiskList) {
    return null;
  }

  // Don't show banner while loading or if additional info is already complete
  if (loading || isAdditionalInfoComplete(data?.application)) {
    return null;
  }

  const handleEnterInfo = () => {
    openModal({
      name: MODAL_NAMES.ADDITIONAL_INFO_MODAL
    });
  };

  return (
    <BannerWrapperStyled>
      <Banner
        clickstreamProps={{
          bannerDisplayed: {
            ...defaultPayloadBannerDisplayed,
            bannerType: 'alert',
            elementName: 'additional_info_required_banner'
          }
        }}
        slotProps={{
          primaryAction: {
            label: 'Enter info',
            clickstreamProps: {
              ...defaultPayloadButtonPressed,
              elementName: 'enter_additional_info'
            },
            onClick: handleEnterInfo
          }
        }}
        slots={{
          startIcon: (
            <SharedAsset
              alt=""
              color="#000000"
              src="/s2/icon/warning-sign-fill.svg"
              width={34}
            />
          )
        }}
        subtitle="Your account needs to confirm details."
        title="Additional banking info required"
        variant="alert"
      />
    </BannerWrapperStyled>
  );
}
